import { __assign } from "tslib";
import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import { BiEventType, BiDataHook } from '@wix/da-bi/pkg/constants';
import IconButtonWithContextMenu from '../../../Dropdown/Preset/IconButtonWithContextMenu';
import { CommentContext } from '../../CommentContext';
import { isActionable } from '../../utils';
import { useButtonConditionals } from '../CommentFooter/useButtonConditionals';
import { useVerification } from '../../../VerificationPopup/withVerification';
import s from './ActionsMenu.scss';
var ActionsMenu = function (_a) {
    var className = _a.className, itemClassName = _a.itemClassName, onHide = _a.onHide, onUnhide = _a.onUnhide, onReportSpam = _a.onReportSpam, onFeature = _a.onFeature, onUnfeature = _a.onUnfeature, onBlockUser = _a.onBlockUser, isCommentReduxReady = _a.isCommentReduxReady, canPostComment = _a.canPostComment;
    var t = useTranslation().t;
    var isMobile = useContext(MobileContext);
    var is_comment_thread = useContext(BiContext).is_comment_thread;
    var _b = useContext(CommentContext), comment = _b.comment, setEditingState = _b.setEditingState, canShowActionMenu = _b.canShowActionMenu;
    var isFeatured = comment.isFeatured, isHidden = comment.isHidden, isOwner = comment.isOwner, isAuthor = comment.isAuthor, isReportable = comment.isReportable;
    var showEditButton = useButtonConditionals({ canPostComment: canPostComment }).showEditButton;
    var restrictToVerified = useVerification().restrictToVerified;
    var onEdit = restrictToVerified('comment', function () {
        return setEditingState('editing');
    });
    var menuItems = useMemo(function () {
        var _a;
        var baseBiData = {
            evid: BiEventType.COMMENT_ACTION,
            itemid: comment.itemId,
            typeid: comment.typeId,
            commentid: comment.commentId,
            commenterid: (_a = comment.user) === null || _a === void 0 ? void 0 : _a.useridUuid,
        };
        var onToggleHide = function () {
            if (!comment.isHidden) {
                onHide && onHide(comment, is_comment_thread === 1);
            }
            else {
                onUnhide && onUnhide(comment);
            }
        };
        return [
            isOwner &&
                isFeatured && {
                label: t("comment.popover.unfeature".concat(isMobile ? '.mobile' : '')),
                dataHook: BiDataHook.COMMENT_FEATURE_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: onUnfeature && (function () { return onUnfeature(comment); }),
                biData: __assign(__assign({}, baseBiData), { menu_item_name: 'unfeature' }),
            },
            isOwner &&
                !isFeatured && {
                label: t("comment.popover.feature".concat(isMobile ? '.mobile' : '')),
                dataHook: BiDataHook.COMMENT_FEATURE_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: onFeature && (function () { return onFeature(comment); }),
                biData: __assign(__assign({}, baseBiData), { menu_item_name: 'feature' }),
            },
            isAuthor &&
                showEditButton && {
                label: t('comment.popover.edit'),
                dataHook: BiDataHook.COMMENT_EDIT_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: onEdit,
                biData: __assign(__assign({}, baseBiData), { menu_item_name: 'edit' }),
            },
            (isOwner || isAuthor) &&
                !isHidden && {
                label: t('comment.popover.delete'),
                dataHook: BiDataHook.COMMENT_DELETE_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: onToggleHide,
                biData: __assign(__assign({}, baseBiData), { menu_item_name: 'delete' }),
            },
            isOwner &&
                !isAuthor && {
                label: t("comment.popover.block".concat(isMobile ? '.mobile' : '')),
                dataHook: BiDataHook.COMMENT_BLOCK_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: function () {
                    // setTimeout allows actionmenu to be hidden before confirm prompt is shown
                    return setTimeout(function () {
                        if (confirm('Add this user to your block list?') &&
                            onBlockUser &&
                            comment.user) {
                            onBlockUser(comment.user);
                        }
                    });
                },
                biData: __assign(__assign({}, baseBiData), { menu_item_name: 'block' }),
            },
            isReportable &&
                !isAuthor && {
                label: t("comment.popover.report".concat(isMobile ? '.mobile' : '')),
                dataHook: BiDataHook.COMMENT_SPAM_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: function () {
                    // setTimeout allows actionmenu to be hidden before confirm prompt is shown
                    return setTimeout(function () {
                        ddt.log('comments', 'reporting spam', comment);
                        if (confirm('Report this comment as spam?') && onReportSpam) {
                            onReportSpam(comment);
                        }
                    }, 100);
                },
                biData: __assign(__assign({}, baseBiData), { menu_item_name: 'spam' }),
            },
        ];
    }, [
        comment,
        isAuthor,
        isCommentReduxReady,
        isFeatured,
        isHidden,
        isMobile,
        isOwner,
        isReportable,
        is_comment_thread,
        itemClassName,
        onBlockUser,
        onFeature,
        onHide,
        onReportSpam,
        onUnfeature,
        onUnhide,
        t,
    ]);
    if (!isActionable(comment) || !canShowActionMenu) {
        return null;
    }
    return (React.createElement(IconButtonWithContextMenu, { className: classnames(s['actions-menu'], className), placement: isMobile ? 'top-end' : 'top', items: menuItems, "aria-label": t('comment.a11y.popover.label'), usePortal: true },
        React.createElement("div", { "data-hook": BiDataHook.COMMENT_ACTION_MENU },
            React.createElement(EllipsisIcon, { size: IconSize.MEDIUM, disableDefaultColors: true }))));
};
ActionsMenu.displayName = 'ActionsMenu';
export default ActionsMenu;
//# sourceMappingURL=ActionsMenu.js.map